import React, { useMemo, useState } from "react";
import { 
  Container, 
  Button, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Table,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Alert,
  Spinner
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchServicesDeletionLogsRequest } from '../../store/actions';
import TableContainer from "components/Common/TableContainer";
import moment from "moment";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "helpers/firebase/firebaseConfig";

const ServicesDeletionLogs = () => {
  const dispatch = useDispatch();
  const { logs, loading, error } = useSelector((state) => state.servicesDeletionLogs);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState("");

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (log) => {
    setSelectedLog(log);
    setRestoreMessage("");
    toggleModal();
  };

  const handleRestore = async (serviceId) => {
    setIsRestoring(true);
    try {
      const serviceRef = doc(db, "service_provider_services", serviceId);
      await updateDoc(serviceRef, {
        is_deleted: false,
        status: "active",
        restored_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      setRestoreMessage("تم استعادة الخدمة بنجاح!");
      setTimeout(() => {
        dispatch(fetchServicesDeletionLogsRequest());
        setIsModalOpen(false);
      }, 1500);
    } catch (err) {
      setRestoreMessage(`خطأ: ${err.message}`);
    } finally {
      setIsRestoring(false);
    }
  };

  React.useEffect(() => {
    dispatch(fetchServicesDeletionLogsRequest());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "اسم الخدمة",
        accessor: "name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "القسم",
        accessor: "service_category",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "السعر (ر.س)",
        accessor: "service_price",
        Cell: ({ value }) => value ? `${value}` : "N/A",
      },
      {
        Header: "تاريخ الأرشفة",
        accessor: "deleted_at",
        Cell: ({ value }) => value ? moment(value).format("DD/MM/YYYY HH:mm") : "N/A",
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge color="warning" pill>
            <i className="bx bx-archive me-1"></i>
            {value === "archived" ? "مؤرشفة" : value}
          </Badge>
        ),
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button color="primary" size="sm" onClick={() => viewDetails(row.original)}>
            <i className="bx bx-show me-1"></i>
            عرض التفاصيل
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="الخدمات المؤرشفة" breadcrumbItem="سجل الأرشفة" />
        
        <Card className="mb-4">
          <CardBody>
            <div className="d-flex align-items-center gap-2 mb-3">
              <i className="bx bx-info-circle text-info" style={{fontSize: '20px'}}></i>
              <div>
                <h6 className="mb-0">الخدمات المؤرشفة</h6>
                <small className="text-muted">هذه الخدمات تم أرشفتها ولا تظهر في قوائم الخدمات النشطة. جميع الحجوزات والفواتير السابقة محفوظة.</small>
              </div>
            </div>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3 text-muted">جاري التحميل...</p>
          </div>
        ) : error ? (
          <Alert color="danger">
            <i className="bx bx-exclamation-circle me-2"></i>
            حدث خطأ أثناء تحميل البيانات: {error}
          </Alert>
        ) : logs && logs.length > 0 ? (
          <TableContainer
            columns={columns}
            data={logs || []}
            isGlobalFilter={true}
            customPageSize={10}
            className="custom-table"
          />
        ) : (
          <Alert color="info" className="text-center">
            <i className="bx bx-check-circle me-2"></i>
            لا توجد خدمات مؤرشفة حالياً
          </Alert>
        )}

        <ArchivedServiceDetailsModal 
          isOpen={isModalOpen} 
          toggle={toggleModal} 
          log={selectedLog}
          onRestore={handleRestore}
          isRestoring={isRestoring}
          restoreMessage={restoreMessage}
        />
      </Container>
    </div>
  );
};

const ArchivedServiceDetailsModal = ({ isOpen, toggle, log, onRestore, isRestoring, restoreMessage }) => {
  if (!log) return null;

  const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.seconds) {
      return moment.unix(timestamp.seconds).format("DD/MM/YYYY HH:mm");
    }
    return moment(timestamp).format("DD/MM/YYYY HH:mm");
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className="bx bx-archive me-2 text-warning"></i>
        تفاصيل الخدمة المؤرشفة: {log.name || "غير معروفة"}
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {restoreMessage && (
          <Alert color={restoreMessage.includes("خطأ") ? "danger" : "success"}>
            {restoreMessage}
          </Alert>
        )}

        <Card className="mb-3 border-info">
          <CardBody>
            <h5 className="card-title mb-3">
              <i className="bx bx-info-circle me-2 text-info"></i>
              معلومات الخدمة
            </h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">اسم الخدمة</label>
                  <p className="mb-0 fw-bold">{log.name || "N/A"}</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">القسم</label>
                  <p className="mb-0 fw-bold">{log.service_category || "N/A"}</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">النوع</label>
                  <p className="mb-0 fw-bold">{log.service_type || "N/A"}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">السعر</label>
                  <p className="mb-0 fw-bold text-success">{log.service_price || 0} ر.س</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">وقت التنفيذ</label>
                  <p className="mb-0 fw-bold">{log.avg_execute_time || "N/A"} دقيقة</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">تاريخ الإنشاء</label>
                  <p className="mb-0 fw-bold">{formatFirestoreTimestamp(log.createdAt)}</p>
                </div>
              </Col>
            </Row>
            {log.description && (
              <div className="mt-3">
                <label className="text-muted small">الوصف</label>
                <p className="mb-0">{log.description}</p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="border-warning">
          <CardBody>
            <h5 className="card-title mb-3">
              <i className="bx bx-archive me-2 text-warning"></i>
              معلومات الأرشفة
            </h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">تاريخ الأرشفة</label>
                  <p className="mb-0 fw-bold">{formatFirestoreTimestamp(log.deleted_at)}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">تمت الأرشفة بواسطة</label>
                  <p className="mb-0 fw-bold">{log.deleted_by || "النظام"}</p>
                </div>
              </Col>
            </Row>
            <div className="alert alert-info mt-3">
              <i className="bx bx-info-circle me-2"></i>
              <strong>ملاحظة:</strong> جميع الحجوزات والفواتير السابقة لهذه الخدمة محفوظة وسهلة الوصول. يمكنك استعادة الخدمة في أي وقت.
            </div>
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isRestoring}>
          إغلاق
        </Button>
        <Button 
          color="success" 
          onClick={() => onRestore(log.id)}
          disabled={isRestoring}
        >
          {isRestoring ? (
            <>
              <Spinner size="sm" className="me-2" />
              جاري الاستعادة...
            </>
          ) : (
            <>
              <i className="bx bx-undo me-1"></i>
              استعادة الخدمة
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ServicesDeletionLogs;